import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { Box, Link } from '@mui/material';
import { Alert, TextV2 } from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

const ConfirmPurchase = ({
  transactionTotal,
  userUnitBalance,
  postTransactionBalance,
  isInvalidTransaction,
}: {
  transactionTotal: number;
  userUnitBalance: number;
  postTransactionBalance: number;
  isInvalidTransaction: boolean;
}) => {
  const { t } = useTranslation();

  const unitsMessage = isInvalidTransaction ? (
    <Box
      sx={(theme) => ({
        width: '650px',
        [theme.breakpoints.down('lg')]: {
          width: '100%',
        },
      })}
    >
      <Alert severity="error">
        <TextV2
          component="span"
          sx={{ color: 'inherit', fontWeight: 'inherit' }}
        >
          {t(
            'ui.event-management.learners.purchase-profiles.confirm-purchase.need-units',
          )}{' '}
        </TextV2>
        <Link component={RouterLink} to="/account" color="textPrimary">
          <TextV2 component="span" color="primary" variant="bodyBold">
            {t(
              'ui.event-management.learners.purchase-profiles.confirm-purchase.request-units.link-label',
            )}
          </TextV2>
        </Link>
      </Alert>
    </Box>
  ) : (
    <Box
      sx={(theme) => ({
        width: '650px',
        [theme.breakpoints.down('lg')]: {
          width: '100%',
        },
        padding: `0.75rem ${theme.spacing(spacingSizeMap.S)}`,
        background: theme.palette.primary.light,
        borderRadius: '4px',
        '&.invalid': {
          backgroundColor: theme.palette.error.main,
        },
      })}
    >
      <TextV2 component="p" variant="bodyBold">
        {t(
          'ui.event-management.learners.purchase-profiles.confirm-purchase.enough-units',
        )}
      </TextV2>
    </Box>
  );

  return (
    <>
      <Box
        sx={(theme) => ({
          width: '100%',
          margin: `0 0 ${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(0.5)}`,
          paddingTop: theme.spacing(spacingSizeMap.XS),
        })}
      >
        <TextV2 component="h1" variant="h3">
          {t(
            'ui.event-management.learners.purchase-profiles.confirm-purchase.heading',
          )}
        </TextV2>
      </Box>
      <Box
        sx={(theme) => ({
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          margin: `0 ${theme.spacing(0.5)} ${theme.spacing(0.5)} ${theme.spacing(0.5)}`,
          paddingTop: theme.spacing(spacingSizeMap.XS),
        })}
      >
        {unitsMessage}
        <Box
          sx={(theme) => ({
            width: '369px',
            margin: '0 0 2.5rem',
            '> :first-of-type': {
              paddingTop: '0 !important',
            },
            '& div': {
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              padding: '0.875rem 0 0.75rem',
              borderBottom: `1px solid ${theme.palette.grey[400]}`,
              '> :last-child': {
                marginLeft: 'auto',
              },
            },
            '& div:last-child': {
              borderBottom: 'none',
            },
          })}
        >
          <div>
            <TextV2 component="span" color="textSecondary">
              {t('ui.event-management.events.purchase-profiles.unit-balance')}
            </TextV2>
            <TextV2
              component="span"
              variant="bodyBold"
              color={isInvalidTransaction ? 'error' : 'textSecondary'}
            >
              {userUnitBalance}&nbsp;
              {t(
                'ui.event-management.events.assign-chapters.summary-box.units',
              )}
            </TextV2>
          </div>
          <div>
            <TextV2 component="span" variant="bodyBold">
              {t(
                'ui.event-management.events.purchase-profiles.transaction-total',
              )}
            </TextV2>
            <TextV2
              component="span"
              variant="bodyBold"
              data-testid="transaction-total"
            >
              {transactionTotal}&nbsp;
              {t(
                'ui.event-management.events.assign-chapters.summary-box.units',
              )}
            </TextV2>
          </div>
          {!isInvalidTransaction && (
            <div>
              <TextV2 component="span">
                {t(
                  'ui.event-management.events.purchase-profiles.post-transaction-balance',
                )}
              </TextV2>
              <TextV2 component="span" variant="bodyBold">
                {postTransactionBalance}&nbsp;
                {t(
                  'ui.event-management.events.assign-chapters.summary-box.units',
                )}
              </TextV2>
            </div>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ConfirmPurchase;
